'use client'

import { useState } from 'react'
import { useNextAuth } from '../../auth/hooks/use-next-auth'
import { endpoints } from '@/utils/axios'
import axios from 'axios'

/**
 * Facebook Connection Button Component
 * Provides a styled button for connecting/disconnecting Facebook account
 */
export function FacebookLoginButton({ onSuccess, onError, ...props }) {
  const [isLoading, setIsLoading] = useState(false)
  const { isFacebookConnected, connectFacebook, disconnectFacebook } = useNextAuth()

  const handleConnect = async () => {
    try {
      console.log('🔄 Facebook Connect: Starting connection process');
      console.log('📝 Connection details:', {
        timestamp: new Date().toISOString(),
        currentlyConnected: isFacebookConnected,
        userAgent: navigator.userAgent,
        url: window.location.href
      });

      const Response = await axios.get(endpoints.meta.loginFacebook, {
        headers: { Authorization: `Bearer ${sessionStorage.jwt_access_token}` }
      })

      window.location.href = Response.data.data.url

      // await connectFacebook();

      console.log('✅ Facebook Connect: Connection process completed successfully');
    } catch (error) {
      console.error('❌ Facebook Connect: Connection error:', error);
      console.error('🔍 Connection error details:', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    }
  };

  const handleDisconnect = async () => {
    try {
      console.log('🔄 Facebook Disconnect: Starting disconnection process');
      console.log('📝 Disconnection details:', {
        timestamp: new Date().toISOString(),
        currentlyConnected: isFacebookConnected,
        url: window.location.href
      });

      await disconnectFacebook();

      console.log('✅ Facebook Disconnect: Disconnection process completed successfully');
    } catch (error) {
      console.error('❌ Facebook Disconnect: Disconnection error:', error);
      console.error('🔍 Disconnection error details:', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    }
  };

  const handleClick = async () => {
    setIsLoading(true)

    console.log('🎯 Facebook Button: Button clicked');
    console.log('📊 Button state:', {
      isFacebookConnected,
      isLoading,
      action: isFacebookConnected ? 'disconnect' : 'connect',
      timestamp: new Date().toISOString()
    });

    try {
      if (isFacebookConnected) {
        console.log('🔄 Facebook Button: Initiating disconnect flow');
        await handleDisconnect()
      } else {
        console.log('🔄 Facebook Button: Initiating connect flow');
        await handleConnect()
      }

      console.log('✅ Facebook Button: Action completed successfully');
      onSuccess?.()
    } catch (error) {
      console.error('❌ Facebook Button: Action failed:', error);
      console.error('🔍 Button action error details:', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        action: isFacebookConnected ? 'disconnect' : 'connect',
        timestamp: new Date().toISOString()
      });
      onError?.(error)
    } finally {
      setIsLoading(false)
      console.log('🏁 Facebook Button: Loading state cleared');
    }
  }

  const buttonText = isFacebookConnected ? 'Disconnect Facebook' : 'Connect Facebook'
  const buttonColor = isFacebookConnected ? '#42a5f5' : '#1877F2'
  const hoverColor = isFacebookConnected ? '#1e88e5' : '#166FE5'

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        width: '100%',
        minWidth: '280px',
        height: '48px',
        backgroundColor: buttonColor,
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        fontFamily: 'Helvetica, Arial, sans-serif',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        opacity: isLoading ? 0.7 : 1,
        transition: 'all 0.2s ease',
        padding: '0 24px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        ...props.style,
      }}
      onMouseEnter={(e) => {
        if (!isLoading) {
          e.target.style.backgroundColor = hoverColor
          e.target.style.transform = 'translateY(-1px)'
          e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)'
        }
      }}
      onMouseLeave={(e) => {
        if (!isLoading) {
          e.target.style.backgroundColor = buttonColor
          e.target.style.transform = 'translateY(0)'
          e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'
        }
      }}
      onFocus={(e) => {
        e.target.style.outline = `2px solid ${buttonColor}`
        e.target.style.outlineOffset = '2px'
      }}
      onBlur={(e) => {
        e.target.style.outline = 'none'
      }}
      {...props}
    >
      {isLoading ? (
        <>
          <div
            style={{
              width: '20px',
              height: '20px',
              border: '2px solid transparent',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <span>{isFacebookConnected ? 'Disconnecting...' : 'Connecting...'}</span>
          <style jsx>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </>
      ) : (
        <>
          <img
            src="/assets/icons/facebook-logo.png"
            alt="Facebook"
            style={{
              width: '20px',
              height: '20px',
              objectFit: 'contain',
            }}
          />
          <span>{buttonText}</span>
        </>
      )}
    </button>
  )
}